import { API } from '../CommonFunc/amplifyApiCompat';
import { ConsoleLogger } from 'aws-amplify/utils';
import React from "react";
import {Alert, Badge, Button, ButtonGroup, Col, Row, Spinner} from "react-bootstrap";
import {ArrowClockwise, CloudDownload, TrashFill} from "react-bootstrap-icons";
import {DismissibleAlert} from "../Alerts/DismissibleAlert";
import '../CommonCss/TableSmall.css'
import {SYNERGY_FINDER_BUCKET_NAME, synergyFinderApiConsts} from "./Const";
import {DownloadButton} from "../CommonUIComponent/DownloadButton";
import {USER_EMAIL} from "../Auth/LoginUser";
import {ApiCallStatus} from "../CommonFunc/Enum";
import {Pipeline, TanStackSynergyFinderHistoryTable} from "./TanStackSynergyFinderHistoryTable";

const logger = new ConsoleLogger("SynergyFinderHistoryTable.tsx")

interface SynergyFinderHistoryTableProps {
    saveRowDataInParentState(selectedRows: Pipeline[]): void
    refreshToken: string
    showErrorAlert(message: string): void
}

interface SynergyFinderHistoryTableState {
    selectedRows: Pipeline[]
    rows: Pipeline[]
    fetchingStatus: ApiCallStatus
    fetchingErrorMsg: string
    deleteStatus: ApiCallStatus
    deleteErrorMsg: string
    downloadErrorMessage: string
    searchTerm: string
}

export class SynergyFinderHistoryTable extends React.Component<SynergyFinderHistoryTableProps, SynergyFinderHistoryTableState> {
    constructor(props: SynergyFinderHistoryTableProps) {
        super(props);
        this.state = {
            fetchingStatus: ApiCallStatus.Completed,
            fetchingErrorMsg: "",
            deleteStatus: ApiCallStatus.Completed,
            deleteErrorMsg: "",
            selectedRows: [],
            downloadErrorMessage: "",
            rows: [],
            searchTerm: "",
        }
    }

    componentDidMount() {
        this.listAllPipelines()
    }

    handleSearchChange = (term: string) => {
        this.setState({searchTerm: term})
    }

    deletePipelines() {
        if (window.confirm("Delete job, are you sure?")) {
            for (let row of this.state.selectedRows) {
                if (row.owner !== USER_EMAIL) {
                    this.setState({
                        deleteStatus: ApiCallStatus.Error,
                        deleteErrorMsg: "Please check with " + row.owner + " before deleting \"" + row.description + "\""
                    })
                    return
                }
            }
            let selectedUUIDs: string[] = this.state.selectedRows.map(row => row.uuid)
            logger.info("selectedUUIDs = ", selectedUUIDs)
            // TODO - add usage monitoring.
            this.setState({
                deleteStatus: ApiCallStatus.Loading,
                deleteErrorMsg: ""
            }, () => {
                // TODO - API call to delete job that is loading may not work, since pipeline uuid = "", only batch_request_uuid exists in DB
                API.del(synergyFinderApiConsts.API_NAME, "/pipeline", {
                    queryParams: {
                        uuids: selectedUUIDs.join(",")
                    },
                })
                    .then((response: any) => {
                        this.setState({
                            deleteStatus: ApiCallStatus.Completed,
                            deleteErrorMsg: ""
                        })
                        this.refreshTableAndReport()
                    })
                    .catch(err => {
                        this.setState({
                            deleteStatus: ApiCallStatus.Error,
                            deleteErrorMsg: err + "",
                        })
                    })
            })
        }
    }

    listAllPipelines() {
        this.setState({
            fetchingStatus: ApiCallStatus.Loading
        }, () => {
            API.get(synergyFinderApiConsts.API_NAME, "/pipeline", {})
                .then((response: any) => {
                    logger.info("Response from API - ", response)
                    this.setState({
                        fetchingStatus: ApiCallStatus.Completed,
                        fetchingErrorMsg: "",
                        // Need to convert
                        rows: response.data
                    })
                })
                .catch((err: any) => {
                    this.setState({
                        fetchingStatus: ApiCallStatus.Error,
                        fetchingErrorMsg: err + "",
                    })
                })
        })

    }

    refreshTableAndReport() {
        this.listAllPipelines()
        // Also reset report to display nothing
        this.setState({
            selectedRows: []
        }, () => this.props.saveRowDataInParentState(this.state.selectedRows))
    }

    componentDidUpdate(prevProps: Readonly<SynergyFinderHistoryTableProps>, prevState: Readonly<SynergyFinderHistoryTableState>, snapshot?: any) {
        if (this.props.refreshToken !== prevProps.refreshToken) {
            logger.info("Refresh token changed, refresh SynergyFinderHistoryTable")
            this.refreshTableAndReport()
        }
    }

    handleRowSelection = (clickedRows: Pipeline[], isSelect: boolean) => {
        logger.debug("handleRowSelection, clickedRows = ", clickedRows, " isSelect = ", isSelect);

        let updatedSelectedRows: Pipeline[];

        if (isSelect) {
            // Add rows that aren't already selected
            const existingIds = new Set(this.state.selectedRows.map(row => row.uuid));
            const newRows = clickedRows.filter(row => !existingIds.has(row.uuid));
            updatedSelectedRows = [...this.state.selectedRows, ...newRows];
        } else {
            // Remove rows that were deselected
            const deselectedIds = new Set(clickedRows.map(row => row.uuid));
            updatedSelectedRows = this.state.selectedRows.filter(row => !deselectedIds.has(row.uuid));
        }

        this.setState({ selectedRows: updatedSelectedRows }, () => {
            this.props.saveRowDataInParentState(this.state.selectedRows);
        });
    }

    render() {
        const searchTerm = this.state.searchTerm || ""
        let tableContent
        if (this.state.fetchingStatus === ApiCallStatus.Loading) {
            tableContent = (<>
                <Spinner
                    animation="border"
                    size="sm"
                    variant="primary"
                >
                </Spinner>
                <span className="text-primary m-2 fw-bold">Loading all jobs ...</span>
            </>)
        } else {
            tableContent = (
                <TanStackSynergyFinderHistoryTable
                    data={this.state.rows}
                    searchTerm={searchTerm}
                    onSearchChange={this.handleSearchChange}
                    onRowSelect={this.handleRowSelection}
                />
            )
        }

        return (
            <>
                <Row>
                    <Col md={9}><h3>History</h3></Col>
                    <Col md={3}><Button className={"float-end"} variant={"primary"} onClick={this.refreshTableAndReport.bind(this)}><ArrowClockwise /></Button></Col>
                </Row>

                <Row>
                    <Col md={4}>
                        <input
                            type="text"
                            className="form-control"
                            placeholder="Search anything"
                            value={searchTerm}
                            onChange={e => this.handleSearchChange(e.target.value)}
                        />
                    </Col>
                    <Col md={8}>
                        {
                            this.state.selectedRows.length > 0 &&
                            <Alert className={"float-end"} variant={"info"}>
                                <Badge pill bg={"success"}>{this.state.selectedRows.length}</Badge> selected reports for
                                <ButtonGroup size={"sm"}>
                                    <DownloadButton
                                        className={"ms-2"}
                                        s3Bucket={SYNERGY_FINDER_BUCKET_NAME}
                                        s3Keys={this.state.selectedRows.map(row => row.s3KeyOutputExcel)}
                                        icon={<span><CloudDownload /> Batch download</span>}
                                        variant={"success"}
                                        buttonSize={"sm"}
                                    />
                                    <Button
                                        size={"sm"}
                                        variant={"danger"}
                                        onClick={this.deletePipelines.bind(this)}>
                                        {this.state.deleteStatus === ApiCallStatus.Loading ? <Spinner animation={"border"} variant={"light"} size={"sm"} /> : <span><TrashFill/> Batch delete</span>}
                                    </Button>
                                </ButtonGroup>
                            </Alert>
                        }
                    </Col>
                </Row>
                {tableContent}
                {this.state.fetchingErrorMsg !== "" && <DismissibleAlert message={this.state.fetchingErrorMsg}/>}
                {this.state.deleteErrorMsg !== "" && <DismissibleAlert message={this.state.deleteErrorMsg} />}
                {this.state.downloadErrorMessage !== "" && <DismissibleAlert message={this.state.downloadErrorMessage} />}
            </>
        );
    }
}